// src/components/public_utilities/pu/puUtils.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Public Utilities v2 shared UI helpers
// --- UPDATE (Public Utilities v2) — NEW FILE ------------------------------------------------
// usePuColors() hoists every colour-mode value the new tabs need into ONE hook (called at the
// top of each component), plus formatters that show "—" for unknown values (never 0).
// --- END UPDATE ------------------------------------------------------------------------------
import { useColorModeValue } from '@chakra-ui/react';
import { config } from '../../../config';

export const DASH = '—';

export function usePuColors() {
    const text = useColorModeValue('secondaryGray.900', 'white');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const cardBg = useColorModeValue('white', 'navy.700');
    const subtleBg = useColorModeValue('gray.50', 'whiteAlpha.50');
    const hoverBg = useColorModeValue('teal.50', 'whiteAlpha.100');
    const border = useColorModeValue('gray.200', 'whiteAlpha.100');
    const gradient = useColorModeValue('linear(to-r, teal.400, teal.600)', 'linear(to-r, teal.500, teal.700)');
    const accent = useColorModeValue('teal.600', 'teal.300');
    const danger = useColorModeValue('red.600', 'red.300');
    const warn = useColorModeValue('orange.600', 'orange.300');
    const ok = useColorModeValue('green.600', 'green.300');
    const inputBg = useColorModeValue('white', 'navy.800');
    const codeBg = useColorModeValue('gray.100', 'whiteAlpha.200');
    return { text, muted, cardBg, subtleBg, hoverBg, border, gradient, accent, danger, warn, ok, inputBg, codeBg };
}

export const fmtNum = (v, digits = 0) => {
    if (v === null || v === undefined || v === '' || !Number.isFinite(Number(v))) return DASH;
    return Number(v).toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: 0 });
};
export const fmtPct = (v) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? DASH : `${Math.round(Number(v) * 10) / 10}%`);
export const fmtMoney = (v) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? DASH : `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`);
export const fmtDate = (v) => {
    if (!v) return DASH;
    const s = String(v);
    const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T12:00:00`) : new Date(v);
    return Number.isFinite(d.getTime()) ? d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : DASH;
};
export const fmtDateTime = (v) => {
    if (!v) return DASH;
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : DASH;
};
export const daysLabel = (days) => {
    if (days === null || days === undefined || !Number.isFinite(Number(days))) return DASH;
    const d = Number(days);
    if (d === 0) return 'today';
    if (d < 0) return `${-d} day${d === -1 ? '' : 's'} overdue`;
    return `in ${d} day${d === 1 ? '' : 's'}`;
};
export const humanize = (s) => (s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : DASH);

export const STATUS_SCHEME = {
    operational: 'green', maintenance_required: 'orange', under_maintenance: 'purple', damaged: 'red', failed: 'red',
    decommissioned: 'gray', closed: 'gray', planned: 'blue', unknown: 'gray',
};
export const RISK_SCHEME = { critical: 'red', high: 'orange', medium: 'yellow', low: 'green' };
export const CONDITION_SCHEME = { good: 'green', fair: 'yellow', poor: 'orange', critical: 'red' };
export const KIND_SCHEME = {
    internal_team: 'teal', contractor_company: 'purple', independent_contractor: 'pink', utility_company: 'blue',
    government_agency: 'cyan', private_owner: 'orange', other: 'gray',
};
export const PRIORITY_SCHEME = { critical: 'red', high: 'orange', medium: 'yellow', low: 'gray' };
export const ASSET_STATUSES = ['operational', 'maintenance_required', 'under_maintenance', 'damaged', 'failed', 'planned', 'closed', 'decommissioned', 'unknown'];

const TYPE_LABELS = {
    drainage_well: 'Drainage Well', catch_basin: 'Catch Basin', culvert: 'Culvert', manhole: 'Manhole', building: 'Building', sidewalk: 'Sidewalk',
    road: 'Road', bridge: 'Bridge', street_light: 'Street Light', traffic_signal: 'Traffic Signal', pole: 'Utility Pole', generator: 'Generator',
    pump: 'Pump', lift_station: 'Lift Station', valve: 'Valve', hydrant: 'Fire Hydrant', water_main: 'Water Main', meter: 'Meter', tank: 'Storage Tank',
    well: 'Water Well', sensor: 'Sensor', other: 'Other Asset',
};
export const typeLabel = (t, catalog) => {
    const c = catalog && Array.isArray(catalog.types) ? catalog.types.find((x) => x.key === t) : null;
    return (c && c.label) || TYPE_LABELS[t] || humanize(t);
};

/** Roles that may run imports / manage managers and codes (mirrors the backend routes). */
export const canAdminister = (role) => ['admin', 'manager', 'supervisor', 'staff'].includes(String(role || '').toLowerCase());
export const canFieldWrite = (role) => ['admin', 'manager', 'supervisor', 'staff', 'helpdesk', 'tech', 'inspector', 'contractor'].includes(String(role || '').toLowerCase());

/** Asset display code: v2 asset_code, the road code, then a short id. */
export const assetCodeOf = (a) => (a ? a.asset_code || a.metadata?.asset_code || a.metadata?.road_code || (a.id ? String(a.id).slice(0, 8) : DASH) : DASH);

/** Photo URLs are stored as "/uploads/…" on the API server — resolve them against the API origin. */
export const resolveMediaUrl = (url) => {
    if (!url) return null;
    if (/^(https?:|blob:|data:)/i.test(url)) return url;
    try {
        const origin = new URL(config.apiUrl, window.location.origin).origin;
        return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
    } catch (_) { return url; }
};
